import React, { useMemo, useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Space, Input, Select, Switch } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import StandardTable from '../../../components/common/StandardTable';
import { useGetMatGapWhInventory, useGetMatWarehouses, useGetVwMatGapRequestItemOutstanding } from '../../../hooks/genericHooks';
import type { VwMatGapWhInventory, MatWarehouses, VwMatGapRequestItemOutstanding } from '../../../types';
import { formatNumberSmart } from '../../../utils/numberFormat';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

export default function MatGapInventory() {
  const navigate = useNavigate();
  const { data: invData = [], isLoading } = useGetMatGapWhInventory() as any;
  const { data: whData = [] } = useGetMatWarehouses() as any;
  const { data: outData = [], refetch: refetchOutstanding } = useGetVwMatGapRequestItemOutstanding() as any;

  const inventory: VwMatGapWhInventory[] = Array.isArray(invData) ? invData : (invData?.data ?? []);
  const warehouses: MatWarehouses[] = Array.isArray(whData) ? whData : (whData?.data ?? []);
  const outstandingRows: VwMatGapRequestItemOutstanding[] = Array.isArray(outData) ? outData as any : (outData?.data ?? []);

  const whMap = useMemo(() => {
    const m = new Map<number, string>();
    (warehouses || []).forEach(w => m.set(w.id, w.name));
    return m;
  }, [warehouses]);

  // Sum outstanding by (mat_id, requested_wh_id) to align with per-warehouse inventory rows
  const outstandingByMatWh = useMemo(() => {
    const m = new Map<string, number>();
    (outstandingRows || []).forEach(r => {
      const wh = r.requested_wh_id;
      if (wh == null) return; // only count when a requested warehouse is specified
      const key = `${r.mat_id}-${wh}`;
      const prev = m.get(key) || 0;
      m.set(key, prev + Number(r.outstanding_qty || 0));
    });
    return m;
  }, [outstandingRows]);

  // Filters
  const [warehouseId, setWarehouseId] = useState<number | undefined>(undefined);
  const [matQuery, setMatQuery] = useState<string>('');
  const [groupFilter, setGroupFilter] = useState<string>('');
  const [extGroupFilter, setExtGroupFilter] = useState<string>('');
  const [showOutstanding, setShowOutstanding] = useState<boolean>(true);

  const filtered = useMemo(() => {
    const norm = (s: any) => (s == null ? '' : String(s)).trim().toLowerCase();
    return (inventory || []).filter(r => {
      if (warehouseId != null && r.warehouse_id !== warehouseId) return false;
      if (matQuery) {
        const s = norm(matQuery);
        const idMatch = String(r.mat_id).toLowerCase().includes(s);
        const descMatch = norm(r.description).includes(s);
        if (!idMatch && !descMatch) return false;
      }
      if (groupFilter && norm(r.prod_group) !== norm(groupFilter)) return false;
      if (extGroupFilter && norm(r.ext_group) !== norm(extGroupFilter)) return false;
      return true;
    });
  }, [inventory, warehouseId, matQuery, groupFilter, extGroupFilter]);

  const columns: ColumnsType<VwMatGapWhInventory> = useMemo(() => [
    { title: 'Material ID', dataIndex: 'mat_id', key: 'mat_id', width: 120, sorter: (a, b) => String(a.mat_id || '').localeCompare(String(b.mat_id || '')) },
    { title: 'Description', dataIndex: 'description', key: 'description', width: 260, sorter: (a, b) => (a.description || '').localeCompare(b.description || '') },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 100, sorter: (a, b) => (a.unit || '').localeCompare(b.unit || '') },
    { title: 'Group', dataIndex: 'prod_group', key: 'prod_group', width: 180, sorter: (a, b) => (a.prod_group || '').localeCompare(b.prod_group || '') },
    { title: 'Hierarchy (Long)', dataIndex: 'hierarchy_long', key: 'hierarchy_long', width: 220, ellipsis: true, sorter: (a, b) => (a.hierarchy_long || '').localeCompare(b.hierarchy_long || '') },
    { title: 'Ext Group', dataIndex: 'ext_group', key: 'ext_group', width: 160, sorter: (a, b) => (a.ext_group || '').localeCompare(b.ext_group || '') },
    { title: 'Warehouse', dataIndex: 'warehouse_id', key: 'warehouse_id', width: 220, render: (v: number) => whMap.get(v) || v, sorter: (a, b) => (whMap.get(a.warehouse_id) || '').localeCompare(whMap.get(b.warehouse_id) || '') },
    { title: 'On-hand Qty', dataIndex: 'onhand_qty', key: 'onhand_qty', width: 140, align: 'right', render: (v: unknown) => formatNumberSmart(v), sorter: (a, b) => (a.onhand_qty ?? 0) - (b.onhand_qty ?? 0) },
    {
      title: showOutstanding ? 'Outstanding Requests' : 'Total Requested',
      key: 'requests_metric',
      width: 180,
      align: 'right',
      render: (_, r) => {
        if (showOutstanding) {
          const k = `${r.mat_id}-${r.warehouse_id}`;
          return formatNumberSmart(outstandingByMatWh.get(k) || 0);
        }
        // fallback to total requested in inventory row when toggle is off
        return formatNumberSmart((r as any).requests ?? 0);
      },
      // Ensure this computed value is also included in Excel export
      exportValue: (r: any) => {
        if (showOutstanding) {
          const k = `${r.mat_id}-${r.warehouse_id}`;
          return outstandingByMatWh.get(k) || 0;
        }
        return (r as any).requests ?? 0;
      },
      sorter: (a, b) => {
        const getOutstanding = (x: VwMatGapWhInventory) => outstandingByMatWh.get(`${x.mat_id}-${x.warehouse_id}`) || 0;
        const av = showOutstanding ? getOutstanding(a) : ((a as any).requests ?? 0);
        const bv = showOutstanding ? getOutstanding(b) : ((b as any).requests ?? 0);
        return av - bv;
      }
    },
  ], [whMap, outstandingByMatWh, showOutstanding]);

  // Cascaded options
  const whOptions = useMemo(() => {
    // Narrow warehouses by selected group/ext-group using current inventory
    const allowedWh = new Set<number>();
    const norm = (s: any) => (s == null ? '' : String(s)).trim().toLowerCase();
    (inventory || [])
      .filter(r => !groupFilter || norm(r.prod_group) === norm(groupFilter))
      .filter(r => !extGroupFilter || norm(r.ext_group) === norm(extGroupFilter))
      .forEach(r => allowedWh.add(r.warehouse_id));
    // If no filters applied, allow all
    const base = (warehouses || []).filter(w => allowedWh.size === 0 || allowedWh.has(w.id));
    return base.map(w => ({ value: w.id, label: w.name })).sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [warehouses, inventory, groupFilter, extGroupFilter]);
  const groupOptions = useMemo(() => {
    const norm = (s: any) => (s == null ? '' : String(s)).trim();
    const byKey = new Map<string, string>();
    (inventory || [])
      .filter(r => warehouseId == null || r.warehouse_id === warehouseId)
      .filter(r => !extGroupFilter || norm(r.ext_group).toLowerCase() === norm(extGroupFilter).toLowerCase())
      .forEach(r => {
        const raw = norm(r.prod_group);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values())
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({ value: v, label: v }));
  }, [inventory, warehouseId, extGroupFilter]);

  const extGroupOptions = useMemo(() => {
    const norm = (s: any) => (s == null ? '' : String(s)).trim();
    const byKey = new Map<string, string>();
    (inventory || [])
      .filter(r => warehouseId == null || r.warehouse_id === warehouseId)
      .filter(r => !groupFilter || norm(r.prod_group).toLowerCase() === norm(groupFilter).toLowerCase())
      .forEach(r => {
        const raw = norm(r.ext_group);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values())
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({ value: v, label: v }));
  }, [inventory, warehouseId, groupFilter]);

  // Auto-clear invalid selections when cascading reduces available options
  React.useEffect(() => {
    if (groupFilter && !groupOptions.some(o => (o.value as string) === groupFilter)) {
      setGroupFilter('');
    }
  }, [groupOptions, groupFilter]);

  React.useEffect(() => {
    if (extGroupFilter && !extGroupOptions.some(o => (o.value as string) === extGroupFilter)) {
      setExtGroupFilter('');
    }
  }, [extGroupOptions, extGroupFilter]);

  React.useEffect(() => {
    if (warehouseId != null && !whOptions.some(o => o.value === warehouseId)) {
      setWarehouseId(undefined);
    }
  }, [whOptions, warehouseId]);

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>GAP Warehouse Inventory</Title>
              <Text type="secondary">On-hand per Material and Warehouse</Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space>
            <Select
              placeholder="Warehouse"
              allowClear
              value={warehouseId}
              onChange={(v) => setWarehouseId(v)}
              options={whOptions}
              style={{ width: 240 }}
            />
            <Select
              placeholder="Group"
              allowClear
              showSearch
              value={groupFilter || undefined}
              onChange={(v) => setGroupFilter(v || '')}
              options={groupOptions}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Ext Group"
              allowClear
              showSearch
              value={extGroupFilter || undefined}
              onChange={(v) => setExtGroupFilter(v || '')}
              options={extGroupOptions}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              style={{ width: 200 }}
            />
            <Input
              placeholder="Material ID or Description"
              allowClear
              value={matQuery}
              onChange={(e) => setMatQuery(e.target.value)}
              style={{ width: 240 }}
            />
            <Button onClick={() => { setWarehouseId(undefined); setMatQuery(''); setGroupFilter(''); setExtGroupFilter(''); }}>Reset</Button>
          </Space>
        </Col>
      </Row>
      <Row style={{ marginBottom: 8 }}>
        <Col span={24}>
          <Space>
            <Text strong>Show Outstanding Requests</Text>
            <Switch checked={showOutstanding} onChange={setShowOutstanding} />
          </Space>
        </Col>
      </Row>
      <StandardTable<VwMatGapWhInventory>
        columns={columns}
        dataSource={filtered}
        rowKey={(r) => `${r.mat_id}-${r.warehouse_id}`}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
}
